<template>
	<BaseAutoComplete
		v-bind="{ ...attrsWithoutModel, ...searchFieldProps(searchText) }"
		:loading="loading"
		:items="sortedItems"
		:auto-select-first="false"
		item-title="name"
		multiple
		chips
		closable-chips
		:disabled="disabled"
		:label="label"
		:rules="[validateRule]"
		:messages="isSearchTooLong ? [`New tag names must be ${TAG_MAX_LENGTH} characters or fewer.`] : []"
		:model-value="internalItems"
		v-model:search="searchText"
		v-test="'tags-combobox'"
		class="w-100 small-pill-input"
		@update:modelValue="onInternalUpdate"
		@focus="handleFocus">
		<template #chip="{ item, index, props: chipProps }">
			<!-- LSP-19649: `link="false"` forces Vuetify's isClickable off. The chip body does nothing —
			     only the pencil and the X act — but the `@click.stop` below still has to stay so a chip
			     click does not bubble to the field and toggle the menu, and an empty handler is enough
			     for Vuetify to paint cursor:pointer, a ripple and tabindex="0" over the whole chip.
			     The Contact combobox in the same About form has never advertised its chip body; this
			     makes Tags consistent with it. -->
			<v-chip
				v-if="maxVisibleSelected === undefined || index < maxVisibleSelected"
				v-bind="chipProps"
				:link="false"
				variant="tonal"
				close-icon="icon-close-small"
				:closable="!disabled"
				:color="item.raw?.baseColor"
				:title="item.raw?.name"
				@click:close="removeItem(item.raw)"
				@click.stop="">
				<span class="text-truncate chip-text">{{ item.raw?.name }}</span>
				<v-icon v-if="!disabled && item.raw?.id" class="cursor-pointer ms-1" @click.stop="handleChipEdit(item.raw)"
					>icon-pencil-edit-solid</v-icon
				>
			</v-chip>
			<!-- LSP-20406: compact-selection mode — the field shows the first maxVisibleSelected chips
			     plus this "+N" summary (the SingleLineDisplay overflow convention) so the field never
			     grows with the selection; the dropdown list stays the place to view and edit the rest. -->
			<v-chip
				v-else-if="index === maxVisibleSelected"
				v-test="'tags-combobox-overflow'"
				:link="false"
				:closable="false"
				variant="tonal"
				color="primary"
				:title="overflowTitle"
				@click.stop="">
				+{{ internalItems.length - maxVisibleSelected }}
			</v-chip>
		</template>
		<template #item="{ item, props: itemProps }">
			<v-list-item v-bind="itemProps">
				<template #prepend="{ isSelected }">
					<v-list-item-action style="pointer-events: none">
						<v-checkbox-btn :model-value="isSelected" color="primary" />
					</v-list-item-action>
				</template>
				<template #title>
					<v-chip variant="tonal" :color="item.raw?.baseColor" size="small">
						{{ item.raw?.name }}
					</v-chip>
				</template>
			</v-list-item>
		</template>
		<template #no-data>
			<v-list-item class="pl-2">
				<v-list-item-title>{{ loading ? "Loading tags..." : noDataText }}</v-list-item-title>
			</v-list-item>
		</template>
		<template v-if="shouldShowCreateButton" #append-item>
			<v-list-item class="create-tag-fixed pa-0" @mousedown.prevent @click.stop="handleCreateTag">
				<div class="text-center pt-0 pb-2 w-100">
					<hr class="mb-2" />
					<v-btn block color="transparent" class="text-none create-action-btn">
						Create tag '<span class="create-action-query">{{ searchText }}</span
						>'
					</v-btn>
				</div>
			</v-list-item>
		</template>
		<template v-if="!disabled" #append-inner>
			<span v-if="canCreate" v-test="'tag-create-button'" @mousedown.stop.prevent @click.stop="handleCreateTag">
				<v-tooltip location="top">
					<template #activator="{ props: tooltipProps }">
						<div v-bind="tooltipProps">
							<v-icon color="primary" class="cursor-pointer">icon-plus</v-icon>
						</div>
					</template>
					<span class="text-no-wrap">Create a new tag</span>
				</v-tooltip>
			</span>
		</template>
	</BaseAutoComplete>
	<EditTagDialog ref="editTagDialog" />
</template>

<script lang="ts">
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import { type Label } from "@/types/Label";
	import api from "@/api";
	import ResponseArray from "@/types/ResponseArray";
	import BaseAutoComplete from "@/components/formfields/BaseAutoComplete.vue";
	import EditTagDialog from "@/components/tags/EditTagDialog.vue";
	import { getRandomColor } from "@/helpers/tags";
	import { TAG_MAX_LENGTH } from "@/api/constants";
	import { countCharacters } from "@/helpers/utilities";
	import DeviceUtility from "@/helpers/device";

	type EditTagDialogExpose = {
		open: (defaultName?: string, defaultColor?: string) => Promise<Label | null>;
		editTag: (label: Label) => Promise<Label | null>;
	};

	export default {
		name: "ComboBox",
		components: {
			BaseAutoComplete,
			EditTagDialog
		},
		inheritAttrs: false,
		emits: ["update:modelValue", "change"],
		props: {
			modelValue: {
				type: Array as () => Label[],
				default: () => []
			},
			disabled: {
				type: Boolean,
				default: false
			},
			label: {
				type: String,
				default: "Tags (Optional)"
			},
			targetOrganizationId: {
				type: String,
				default: ""
			},
			loadOnMount: {
				type: Boolean,
				default: true
			},
			zIndex: {
				type: Number,
				default: 9999
			},
			maxTags: {
				type: Number,
				required: false
			},
			/**
			 * LSP-20406: compact selection display. When set, the field renders only the first N
			 * selected chips followed by a "+remaining" chip, so the field keeps a fixed height at
			 * any selection size. Off by default — existing consumers (session About tab, form
			 * builder) keep the full chip stack.
			 */
			maxVisibleSelected: {
				type: Number,
				required: false
			},
			/**
			 * Whether the current user can create new tags. Gates the plus-icon, the
			 * inline "Create tag" footer for both matching and empty results.
			 * Defaults to `true` to preserve admin-only form-builder usage (FormPage,
			 * FormEditorDialog) without forcing those call-sites to opt in. Entity-level
			 * callers exposed to non-admin roles MUST pass `!!isAdmin` — otherwise a
			 * "User" role will see affordances that POST /labels and 403 (LSP-15564).
			 */
			canCreate: {
				type: Boolean,
				default: true
			}
		},
		data() {
			return {
				TAG_MAX_LENGTH,
				countCharacters,
				internalItems: [] as Label[],
				items: [] as Label[],
				// LSP-20845: false until a fetch actually starts. Initializing true left lazy consumers
				// (loadOnMount=false, e.g. the quick-action SessionTagPicker) showing an indefinite
				// loading bar that implied an active search until the field was first focused —
				// loadTags() owns the flag for the real fetch window.
				loading: false,
				currentPage: 1,
				itemsPerPage: 9999,
				itemsOnServer: 0,
				noDataText: "No tags found.",
				labelsDropdownWidth: 0,
				maximumNumberOfTags: this.maxTags ?? null,
				searchText: ""
			};
		},
		computed: {
			isSearchTooLong() {
				return countCharacters(this.searchText) > TAG_MAX_LENGTH;
			},
			overflowTitle(): string {
				if (this.maxVisibleSelected === undefined) return "";
				return this.internalItems
					.slice(this.maxVisibleSelected)
					.map((tag) => tag.name)
					.join(", ");
			},
			sortedItems() {
				if (exceedsSearchLimit(this.searchText)) return [];
				const search = this.searchText.trim().toLowerCase();
				const filtered = search
					? (this.items || []).filter((t) => (t?.name || "").toLowerCase().includes(search))
					: this.items || [];
				return [...filtered].sort(this.sortItems);
			},
			isMobile() {
				return DeviceUtility.isMobile;
			},
			shouldShowCreateButton() {
				// Keep creation available like Contacts; EditTagDialog validates the prefilled name.
				if (this.disabled || !this.canCreate) return false;
				const term = this.searchText.trim();
				if (!term) return false;
				const lower = term.toLowerCase();
				return !(this.items || []).filter(Boolean).some((t) => (t?.name || "").trim().toLowerCase() === lower);
			},
			attrsWithoutModel() {
				const { "onUpdate:modelValue": _omit, ...rest } = this.$attrs as any;
				return rest;
			},
			validateRule() {
				return () => {
					if (!this.maximumNumberOfTags) return true;
					if (this.internalItems.length <= this.maximumNumberOfTags) return true;
					const noun = this.maximumNumberOfTags === 1 ? "tag" : "tags";
					const verb = this.maximumNumberOfTags === 1 ? "is" : "are";
					return `A maximum of ${this.maximumNumberOfTags} ${noun} ${verb} allowed.`;
				};
			}
		},
		watch: {
			targetOrganizationId: {
				immediate: true,
				async handler(newVal, oldVal) {
					if (newVal !== oldVal && newVal) {
						await this.loadTags();
					}
				}
			},
			modelValue: {
				immediate: true,
				handler(newVal) {
					const limit = this.maximumNumberOfTags;
					if (limit && (newVal as Label[]).length > limit) {
						newVal = (newVal as Label[]).slice(-limit);
					}
					this.internalItems = (newVal as Label[])
						.filter((value: Label | string) => typeof value !== "string" || value.trim() !== "")
						.map(this.stringToTag)
						.sort(this.sortItems);
				}
			},
			internalItems: {
				handler(newVal) {
					const filteredItems = newVal.filter((item: Label | string) => this.filterLabelNamesWithoutText(item));

					if (JSON.stringify(filteredItems) !== JSON.stringify(newVal)) {
						this.$nextTick(() => {
							this.internalItems = filteredItems;
						});
						return;
					}

					const sorted = filteredItems.map(this.stringToTag).sort(this.sortItems);

					if (JSON.stringify(sorted) !== JSON.stringify(this.modelValue)) {
						this.$emit("update:modelValue", sorted);
						this.$emit("change", sorted);
					}
				},
				deep: true
			}
		},
		async beforeMount() {
			if (this.loadOnMount) {
				await this.loadTags();
			}
		},
		methods: {
			searchFieldProps,
			async handleFocus() {
				this.labelsDropdownWidth = (this.$el as HTMLElement)?.offsetWidth;
				await this.loadTags();
			},
			onInternalUpdate(newValue: Label[]) {
				const filtered = (newValue || []).filter((item) => this.filterLabelNamesWithoutText(item));
				let processed = this.deduplicateItems(filtered.map(this.stringToTag));
				const limit = this.maximumNumberOfTags;
				if (limit && processed.length > limit) {
					processed = processed.slice(-limit);
				}
				this.internalItems = processed;
			},
			async handleChipEdit(tag: Label) {
				const dialog = this.$refs.editTagDialog as EditTagDialogExpose | null;
				if (!dialog) return;
				const record = await dialog.editTag(tag);
				if (record) {
					this.updateItem(record);
				}
			},
			async handleCreateTag() {
				const dialog = this.$refs.editTagDialog as EditTagDialogExpose | null;
				if (!dialog) return;
				const record = await dialog.open(this.searchText.trim());
				if (record) {
					const existing = this.items.some((x) => x.id === record.id);
					if (!existing) {
						this.items = [record, ...this.items];
					}
					const alreadySelected = this.internalItems.some((x) => x.id === record.id);
					if (!alreadySelected) {
						this.internalItems = [...this.internalItems, record];
					}
					this.searchText = "";
				}
			},
			updateItem(item: Label) {
				const idx = this.items.findIndex((x) => x.id === item.id);
				if (idx !== -1) {
					this.items = [...this.items.slice(0, idx), item, ...this.items.slice(idx + 1)];
				}
				const selIdx = this.internalItems.findIndex((x) => x.id === item.id);
				if (selIdx !== -1) {
					this.internalItems = [...this.internalItems.slice(0, selIdx), item, ...this.internalItems.slice(selIdx + 1)];
				}
			},
			filterLabelNamesWithoutText(item: string | Label) {
				if (typeof item === "string") {
					return item.trim() !== "";
				}
				return item?.name?.trim() !== "";
			},
			deduplicateItems(items: Array<Label>) {
				return items.filter((item, index, self) => {
					if (item.id) {
						return index === self.findIndex((t) => t.id === item.id);
					}
					return index === self.findIndex((t) => !t.id && t.name.toLowerCase() === item.name.toLowerCase());
				});
			},
			async loadTags() {
				this.loading = true;
				try {
					const resp = (await (api as any).getLabels(
						{
							page: this.currentPage,
							pageSize: this.itemsPerPage,
							where: {},
							order: [["createdAt", "DESC NULLS FIRST"]]
						},
						undefined,
						this.targetOrganizationId
							? {
									headers: {
										"x-target-organization-ids": [this.targetOrganizationId]
									}
								}
							: undefined
					)) as ResponseArray<Label>;
					this.itemsOnServer = resp.count;
					this.items = resp.rows;
				} catch (err) {
					console.error("Error loading tags", err);
					this.noDataText = "There was an issue loading your organization's tags. Please refresh the page to try again.";
				} finally {
					this.loading = false;
				}
			},
			// This saves an API hit when conversations or media or projects save.
			replaceSavedTags(tags: Label[]) {
				this.items = this.items.map((item) => {
					if (!item.id) {
						const tag = tags.find((tag) => (tag.name || "").trim().toLowerCase() === (item.name || "").trim().toLowerCase());
						if (tag) {
							return tag;
						}
						return item;
					}
					return item;
				});
			},
			// Method to force processing of any pending input before validation/save
			processPendingInput() {
				const trimmed = this.searchText.trim();
				if (!trimmed || countCharacters(trimmed) > TAG_MAX_LENGTH) return;
				const alreadySelected = this.internalItems.some(
					(item) => ((typeof item === "string" ? item : item.name) || "").trim().toLowerCase() === trimmed.toLowerCase()
				);
				if (!alreadySelected) {
					const newTag = { name: trimmed, baseColor: getRandomColor(), selected: true } as Label;
					if (!this.items.some((x) => (x.name || "").trim().toLowerCase() === trimmed.toLowerCase())) {
						this.items.push(newTag);
					}
					this.internalItems = [...this.internalItems, newTag];
					this.$emit("update:modelValue", this.internalItems);
					this.$emit("change", this.internalItems);
				}
			},
			stringToTag(v: string | Label): Label {
				if (typeof v === "string") {
					const normalized = v.trim();
					const existing = this.items.find((i) => (i.name || "").trim().toLowerCase() === normalized.toLowerCase());
					if (!existing) {
						const newTag = { name: normalized, baseColor: getRandomColor() } as Label;
						if (!this.items.some((x) => (x.name || "").trim().toLowerCase() === normalized.toLowerCase())) {
							this.items.push(newTag);
						}
						return newTag;
					}
					return existing;
				}
				return v;
			},
			sortItems(a: Label, b: Label) {
				const aSelected = this.internalItems.some((item) => (typeof item === "object" ? item.id === a.id : item === a));
				const bSelected = this.internalItems.some((item) => (typeof item === "object" ? item.id === b.id : item === b));

				if (aSelected && !bSelected) return -1;
				if (!aSelected && bSelected) return 1;

				return a.name.localeCompare(b.name);
			},
			// Still used in web forms
			validate() {
				if (!this.maximumNumberOfTags) {
					return true;
				}
				return this.internalItems.length <= this.maximumNumberOfTags;
			},
			removeItem(item: Label) {
				const index = this.internalItems.findIndex((i) => (i.id && i.id === item.id) || i.name === item.name);
				if (index !== -1) {
					const newItems = [...this.internalItems];
					newItems.splice(index, 1);
					this.internalItems = newItems;
				}
			}
		}
	};
</script>

<style scoped>
	/* LSP-19649: the chip sits inside `.v-field__input`, which Vuetify paints `cursor: text`
	   (VAutocomplete.css: `.v-autocomplete .v-field .v-field__input`). Inheritance gave the chip
	   body and its label the text I-beam, as if the tag were selectable copy. The chip body does
	   nothing, so it takes the ordinary arrow; the two affordances that DO act keep their pointer
	   from their own rules (`.cursor-pointer` on the pencil, Vuetify's `.v-chip__close` on the X).
	   `:not(.v-chip--link)` so a genuinely clickable chip elsewhere is never flattened, and scoped to
	   `.v-autocomplete__selection` so it cannot reach the chips the dropdown renders inside a
	   clickable `v-list-item` (Bugbot, commit bd4db2d): those correctly inherit the row's pointer,
	   because clicking that row really does select the tag. */
	.v-autocomplete__selection .v-chip:not(.v-chip--link) {
		cursor: default;
	}

	.create-tag-fixed {
		position: sticky !important;
		bottom: 0;
		background-color: white;
	}

	.chip-text {
		max-width: 120px;
	}
</style>
